import React from 'react';
import { motion } from 'framer-motion';
import { Star, ExternalLink, MapPin, Shield } from 'lucide-react';
import { REVIEW_SOURCES, BUSINESS_INFO } from '../data/reviewSources';

// Google Icon SVG
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Justdial Icon SVG
const JustdialIcon = () => (
  <svg viewBox="0 0 100 100" width="24" height="24">
    <circle cx="50" cy="50" r="45" fill="#FFD700"/>
    <text x="50" y="65" textAnchor="middle" fontSize="40" fontWeight="bold" fill="#000">JD</text>
  </svg>
);

// Star Rating Component
const StarRating = ({ rating, size = 18 }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          size={size}
          style={{
            color: i < fullStars || (i === fullStars && hasHalfStar) ? '#F59E0B' : '#E5E7EB',
            fill: i < fullStars ? '#F59E0B' : (i === fullStars && hasHalfStar ? 'url(#half)' : 'none')
          }}
        />
      ))}
    </div>
  );
};

// Review Platform Card
const ReviewCard = ({ platform, rating, reviewCount, reviewCountDisplay, url, icon, delay }) => {
  const Icon = icon === 'google' ? GoogleIcon : JustdialIcon;
  const displayCount = reviewCountDisplay || `${reviewCount}+`;
  
  return (
    <motion.a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true }}
      className="block p-6 rounded-2xl transition-all hover:shadow-lg hover:-translate-y-1"
      style={{ 
        backgroundColor: 'white',
        border: '1px solid #E5E7EB'
      }}
      data-testid={`${platform.toLowerCase()}-review-card`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-shrink-0">
          <Icon />
        </div>
        <span className="font-semibold text-lg" style={{ color: '#1F2937' }}>
          {platform}
        </span>
      </div>
      
      <div className="flex items-center gap-2 mb-2">
        <span className="text-3xl font-bold" style={{ color: '#1F2937' }}>
          {rating}
        </span>
        <StarRating rating={rating} />
      </div>
      
      <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
        {displayCount} {platform === 'Justdial' ? 'ratings' : 'reviews'}
      </p>
      
      <div 
        className="inline-flex items-center gap-2 text-sm font-medium"
        style={{ color: icon === 'google' ? '#4285F4' : '#B8860B' }}
      >
        View on {platform}
        <ExternalLink size={14} />
      </div>
    </motion.a>
  );
};

const TrustedReviews = () => {
  const google = REVIEW_SOURCES.google;
  const justdial = REVIEW_SOURCES.justdial;

  return (
    <section 
      className="py-12 sm:py-16" 
      style={{ backgroundColor: '#F9FAFB' }}
      data-testid="trusted-reviews-section"
    >
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ backgroundColor: '#ECFDF5' }}>
            <Shield size={18} style={{ color: '#059669' }} />
            <span className="text-sm font-medium" style={{ color: '#059669' }}>Verified Reviews</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3" style={{ color: '#1F2937' }}>
            Trusted by Pet Parents in Noida
          </h2>
          <p className="text-base max-w-2xl mx-auto" style={{ color: '#6B7280' }}>
            See what customers are saying across Google and Justdial
          </p>
        </div>

        {/* Review Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto mb-8">
          <ReviewCard
            platform={google.platform}
            rating={google.rating}
            reviewCount={google.reviewCount}
            url={google.profileUrl}
            icon={google.icon}
            delay={0}
          />
          <ReviewCard
            platform={justdial.platform}
            rating={justdial.rating}
            reviewCount={justdial.reviewCount}
            reviewCountDisplay={justdial.reviewCountDisplay}
            url={justdial.listingUrl}
            icon={justdial.icon}
            delay={0.1}
          />
        </div>

        {/* Location Info */}
        <div className="text-center">
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ backgroundColor: 'white', border: '1px solid #E5E7EB' }}
          >
            <MapPin size={16} style={{ color: '#0EA5E9' }} />
            <span className="text-sm" style={{ color: '#6B7280' }}>
              {BUSINESS_INFO.name}, {BUSINESS_INFO.address.sector}, {BUSINESS_INFO.address.city}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustedReviews;
